import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { LoadingState, InputNumber } from "components";
import { useReceipt, useCustomer } from "hooks";
import { Receipt, ReceiptForm, ReceiptItem } from "types";
import SnackbarUtils from "utils/SnackbarUtils";
import { ROUTES } from "utils";
import { ArrowLeft, Plus, Save, Trash2 } from "lucide-react";
import { debounce } from "utils/helpers/data_helpers";

const todayString = () => new Date().toISOString().slice(0, 10);
const ATTACHMENT_SPLIT_REGEX = /[,\n]/;

const defaultFormValues: ReceiptForm = {
  receipt_code: "",
  receipt_date: todayString(),
  object_name: "",
  payer_name: "",
  address: "",
  description: "",
  attachments: [],
  original_voucher_no: "",
  export_invoice_id: undefined,
  items: [],
};

const normalizeNumber = (value: unknown): number => {
  if (value === null || value === undefined || value === "") return 0;
  if (typeof value === "number" && Number.isFinite(value)) return value;
  const parsed = Number(String(value).replace(/,/g, ""));
  return Number.isFinite(parsed) ? parsed : 0;
};

const normalizeAttachments = (value: unknown): string[] => {
  if (Array.isArray(value)) {
    return value
      .map((item) => (typeof item === "string" ? item : String(item ?? "")))
      .map((item) => item.trim())
      .filter(Boolean);
  }

  if (typeof value === "string") {
    const trimmed = value.trim();
    if (!trimmed) return [];

    try {
      const parsed = JSON.parse(trimmed);
      if (Array.isArray(parsed)) {
        return parsed
          .map((item) => (typeof item === "string" ? item : String(item ?? "")))
          .map((item) => item.trim())
          .filter(Boolean);
      }
    } catch (error) {
      // ignore invalid JSON strings
    }

    return trimmed
      .split(ATTACHMENT_SPLIT_REGEX)
      .map((item) => item.trim())
      .filter(Boolean);
  }

  return [];
};

const sanitizeItems = (items?: ReceiptItem[]): ReceiptItem[] =>
  Array.isArray(items)
    ? items
        .map((item) => {
          const normalizedAmount = normalizeNumber(item.amount);
          const partnerIdValue =
            item.partner_id !== undefined &&
            item.partner_id !== null &&
            String(item.partner_id).trim() !== "" &&
            Number.isFinite(Number(item.partner_id))
              ? Number(item.partner_id)
              : undefined;

          const vatRate =
            item.vat_rate !== undefined &&
            item.vat_rate !== null &&
            String(item.vat_rate).trim() !== "" &&
            Number.isFinite(Number(item.vat_rate))
              ? Number(item.vat_rate)
              : undefined;

          const pitRate =
            item.pit_rate !== undefined &&
            item.pit_rate !== null &&
            String(item.pit_rate).trim() !== "" &&
            Number.isFinite(Number(item.pit_rate))
              ? Number(item.pit_rate)
              : undefined;

          return {
            amount: normalizedAmount,
            ...(partnerIdValue !== undefined
              ? { partner_id: partnerIdValue }
              : {}),
            reason: item.reason ? String(item.reason).trim() : undefined,
            vat_rate: vatRate,
            pit_rate: pitRate,
          };
        })
        .filter(
          (item) =>
            item.amount ||
            item.partner_id ||
            Boolean(item.reason) ||
            item.vat_rate ||
            item.pit_rate,
        )
    : [];

const ReceiptDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);

  const { fetchDetail, createReceipt, updateReceipt } = useReceipt();
  const { data: customersData } = useCustomer().fetchList(
    { page: 1, pageSize: 200 },
    true,
  );

  const { data: detailResponse, isFetching: isFetchingDetail } = fetchDetail(
    id ?? "",
    isEdit,
  );

  const [form, setForm] = useState<ReceiptForm>(defaultFormValues);
  const [attachmentsInput, setAttachmentsInput] = useState<string[]>([]);
  const [attachmentsText, setAttachmentsText] = useState<string>("");
  const [existingAttachments, setExistingAttachments] = useState<string[]>([]);
  const [items, setItems] = useState<ReceiptItem[]>([]);

  useEffect(() => {
    if (!detailResponse?.data) return;
    const receipt = detailResponse.data as Receipt;
    const normalizedAttachments = normalizeAttachments(receipt.attachments);
    const sanitizedItems = sanitizeItems(receipt.items);
    setForm({
      receipt_code: receipt.receipt_code || "",
      receipt_date: receipt.receipt_date || todayString(),
      object_name: receipt.object_name || "",
      payer_name: receipt.payer_name || "",
      address: receipt.address || "",
      description: receipt.description || "",
      attachments: normalizedAttachments,
      original_voucher_no: receipt.original_voucher_no || "",
      export_invoice_id: receipt.export_invoice_id,
      items: sanitizedItems,
    });
    setExistingAttachments(normalizedAttachments);
    setAttachmentsInput(normalizedAttachments);
    setAttachmentsText(normalizedAttachments.join(", "));
    setItems(
      sanitizedItems.length
        ? sanitizedItems
        : [
            {
              amount: 0,
              partner_id: null,
              reason: "",
              vat_rate: undefined,
              pit_rate: undefined,
            },
          ],
    );
  }, [detailResponse]);

  useEffect(() => {
    if (isEdit) return;
    setForm(defaultFormValues);
    setItems([
      {
        amount: 0,
        partner_id: null,
        reason: "",
        vat_rate: undefined,
        pit_rate: undefined,
      },
    ]);
    setAttachmentsInput([]);
    setAttachmentsText("");
    setExistingAttachments([]);
  }, [isEdit]);

  const totalAmount = useMemo(
    () => items.reduce((sum, item) => sum + (Number(item.amount ?? 0) || 0), 0),
    [items],
  );

  const handleFormChange = useCallback((key: keyof ReceiptForm, value: any) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  }, []);

  const handleAddItem = useCallback(() => {
    setItems((prev) => [
      ...prev,
      {
        amount: 0,
        partner_id: null,
        reason: "",
        vat_rate: undefined,
        pit_rate: undefined,
      },
    ]);
  }, []);

  const handleItemChange = useCallback(
    (index: number, key: keyof ReceiptItem, value: any) => {
      setItems((prev) =>
        prev.map((item, idx) =>
          idx === index
            ? {
                ...item,
                [key]:
                  key === "amount" || key === "vat_rate" || key === "pit_rate"
                    ? normalizeNumber(value)
                    : value,
              }
            : item,
        ),
      );
    },
    [],
  );
  const debouncedItemChange = useCallback(
    (index: number, key: keyof ReceiptItem, value: any) =>
      handleItemChange(index, key, value),
    [handleItemChange],
  );
  const debouncedAttachmentsParse = useMemo(
    () =>
      debounce((text: string) => {
        const parsed = text
          .split(ATTACHMENT_SPLIT_REGEX)
          .map((v) => v.trim())
          .filter(Boolean);
        setAttachmentsInput(parsed);
      }, 250),
    [],
  );

  useEffect(() => {
    debouncedAttachmentsParse(attachmentsText);
  }, [attachmentsText, debouncedAttachmentsParse]);

  const customerOptions =
    (customersData?.data as any[])?.map((c) => ({
      value: c.id,
      label: `${c.code}${c.name ? ` - ${c.name}` : ""}`,
    })) ?? [];

  const handleRemoveItem = useCallback((index: number) => {
    setItems((prev) => prev.filter((_, idx) => idx !== index));
  }, []);

  const buildPayload = useCallback((): ReceiptForm | null => {
    if (!form.receipt_date || !form.object_name || !form.payer_name) {
      SnackbarUtils.error(
        "Ngày phiếu thu, Đơn vị/Đối tượng và Người nộp là bắt buộc",
      );
      return null;
    }

    if (isEdit && !form.receipt_code) {
      SnackbarUtils.error("Mã phiếu thu là bắt buộc khi cập nhật");
      return null;
    }

    const normalizedItems = sanitizeItems(items);

    if (!normalizedItems.length || !normalizedItems[0].amount) {
      SnackbarUtils.error("Vui lòng nhập ít nhất một dòng chi tiết và số tiền");
      return null;
    }

    return {
      ...form,
      attachments:
        attachmentsInput.length > 0
          ? normalizeAttachments(attachmentsInput)
          : existingAttachments,
      items: normalizedItems,
    };
  }, [attachmentsInput, existingAttachments, form, isEdit, items]);

  const handleSubmit = useCallback(
    (event?: React.FormEvent) => {
      event?.preventDefault();
      const payload = buildPayload();
      if (!payload) return;

      if (isEdit && id) {
        updateReceipt.mutate(
          { id: +id, ...payload },
          {
            onSuccess: () => {
              navigate(ROUTES.ACCOUNTING.CASH_RECEIPT);
            },
          },
        );
        return;
      }

      createReceipt.mutate(payload, {
        onSuccess: () => {
          navigate(ROUTES.ACCOUNTING.CASH_RECEIPT);
        },
      });
    },
    [buildPayload, createReceipt, id, isEdit, navigate, updateReceipt],
  );

  const isSaving = createReceipt.isPending || updateReceipt.isPending;

  return (
    <>
      {(isFetchingDetail || isSaving) && (
        <LoadingState
          message={isEdit ? "Đang tải phiếu thu" : "Đang lưu phiếu thu"}
          description="Vui lòng chờ trong giây lát."
        />
      )}
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-500 uppercase">Phiếu thu</p>
            <h1 className="text-2xl font-semibold text-gray-900">
              {isEdit ? "Cập nhật phiếu thu" : "Thêm phiếu thu"}
            </h1>
            <p className="text-sm text-gray-600">
              Nhập thông tin chung và danh sách dòng chi tiết như mẫu.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => navigate(ROUTES.ACCOUNTING.CASH_RECEIPT)}
              className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              <ArrowLeft className="h-4 w-4" />
              Danh sách phiếu thu
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-70"
              disabled={isSaving}
            >
              <Save className="h-4 w-4" />
              {isSaving ? "Đang lưu..." : "Lưu phiếu thu"}
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="border-b border-gray-200 px-4 py-3">
              <h2 className="text-base font-semibold text-gray-900">
                Thông tin chung
              </h2>
            </div>
            <div className="grid gap-4 px-4 py-4 md:grid-cols-2">
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Mã phiếu thu (tự sinh nếu để trống)
                </label>
                <input
                  value={form.receipt_code}
                  onChange={(e) =>
                    handleFormChange("receipt_code", e.target.value)
                  }
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="PT00001 (tự sinh nếu không nhập)"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Ngày phiếu thu *
                </label>
                <input
                  type="date"
                  value={form.receipt_date}
                  onChange={(e) =>
                    handleFormChange("receipt_date", e.target.value)
                  }
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Đơn vị/Đối tượng *
                </label>
                <input
                  value={form.object_name}
                  onChange={(e) =>
                    handleFormChange("object_name", e.target.value)
                  }
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Khách lẻ"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Người nộp *
                </label>
                <input
                  value={form.payer_name}
                  onChange={(e) =>
                    handleFormChange("payer_name", e.target.value)
                  }
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Nhập người nộp"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Địa chỉ
                </label>
                <input
                  value={form.address ?? ""}
                  onChange={(e) => handleFormChange("address", e.target.value)}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Nhập địa chỉ"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Kèm theo (nhiều tệp)
                </label>
                <div className="space-y-2">
                  <input
                    type="text"
                    value={attachmentsText}
                    onChange={(e) => {
                      setAttachmentsText(e.target.value);
                    }}
                    placeholder="tệp1.png, tệp2.pdf"
                    className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  />
                  {(attachmentsInput.length > 0 ||
                    existingAttachments.length > 0) && (
                    <div className="rounded-md border border-gray-200 bg-gray-50 px-3 py-2">
                      <p className="text-xs font-semibold text-gray-600 mb-1">
                        Tệp đã chọn:
                      </p>
                      <ul className="space-y-1 text-xs text-gray-700 list-disc list-inside">
                        {(attachmentsInput.length > 0
                          ? attachmentsInput
                          : existingAttachments
                        ).map((item, idx) => (
                          <li key={`${idx}`}>{item}</li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Số CT gốc
                </label>
                <input
                  value={form.original_voucher_no ?? ""}
                  onChange={(e) =>
                    handleFormChange("original_voucher_no", e.target.value)
                  }
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Nhập số CT gốc"
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium text-gray-700">
                  Hóa đơn xuất (ID)
                </label>
                <input
                  type="number"
                  value={form.export_invoice_id ?? ""}
                  onChange={(e) =>
                    handleFormChange(
                      "export_invoice_id",
                      e.target.value ? Number(e.target.value) : undefined,
                    )
                  }
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Nhập số HĐ xuất"
                />
              </div>
              <div className="space-y-2 md:col-span-2">
                <label className="text-sm font-medium text-gray-700">
                  Nội dung
                </label>
                <textarea
                  value={form.description ?? ""}
                  onChange={(e) =>
                    handleFormChange("description", e.target.value)
                  }
                  rows={2}
                  className="w-full rounded border border-gray-200 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
                  placeholder="Nội dung thu"
                />
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white shadow-sm">
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h2 className="text-base font-semibold text-gray-900">
                Thông tin chi tiết
              </h2>
              <div className="flex items-center gap-3">
                <div className="text-right">
                  <p className="text-xs text-gray-500">Tổng tiền</p>
                  <p className="text-base font-semibold text-gray-900">
                    {totalAmount.toLocaleString("vi-VN")} đ
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleAddItem}
                  className="inline-flex items-center gap-2 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50"
                >
                  <Plus className="h-4 w-4" />
                  Thêm dòng
                </button>
              </div>
            </div>

            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-sm">
                <thead className="bg-gray-50 text-gray-700">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold">STT</th>
                    <th className="px-3 py-2 text-right font-semibold">
                      Số tiền
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      Khách hàng/NCC
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">
                      Lý do thu
                    </th>
                    <th className="px-3 py-2 text-left font-semibold">VAT %</th>
                    <th className="px-3 py-2 text-left font-semibold">PIT %</th>
                    <th className="px-3 py-2 text-center font-semibold">
                      Thao tác
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {items.length ? (
                    items.map((item, index) => (
                      <tr key={`item-${index}`} className="hover:bg-gray-50">
                        <td className="px-3 py-2 text-gray-500">{index + 1}</td>
                        <td className="px-3 py-2 text-right text-gray-900">
                          <InputNumber
                            value={item.amount ?? 0}
                            onValueChange={(values) =>
                              debouncedItemChange(
                                index,
                                "amount",
                                values.floatValue ?? 0,
                              )
                            }
                            className="h-9 px-2 py-1 text-right text-sm"
                            suffix=" ₫"
                          />
                        </td>
                    <td className="px-3 py-2 text-gray-900">
                      <select
                        value={item.partner_id ?? ""}
                        onChange={(e) =>
                          debouncedItemChange(
                            index,
                            "partner_id",
                            e.target.value,
                          )
                        }
                        className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                      >
                        <option value="">Chọn khách hàng/NCC</option>
                        {customerOptions.map((opt) => (
                          <option key={opt.value} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </td>
                        <td className="px-3 py-2 text-gray-900">
                          <input
                            value={item.reason ?? ""}
                            onChange={(e) =>
                              debouncedItemChange(
                                index,
                                "reason",
                                e.target.value,
                              )
                            }
                            className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                            placeholder="Lý do thu"
                          />
                        </td>
                        <td className="px-3 py-2 text-gray-900">
                          <input
                            type="number"
                            value={item.vat_rate ?? ""}
                            onChange={(e) =>
                              debouncedItemChange(
                                index,
                                "vat_rate",
                                e.target.value,
                              )
                            }
                            className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                            placeholder="0"
                          />
                        </td>
                        <td className="px-3 py-2 text-gray-900">
                          <input
                            type="number"
                            value={item.pit_rate ?? ""}
                            onChange={(e) =>
                              debouncedItemChange(
                                index,
                                "pit_rate",
                                e.target.value,
                              )
                            }
                            className="w-full rounded border border-gray-200 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                            placeholder="0"
                          />
                        </td>
                        <td className="px-3 py-2 text-center">
                          <button
                            type="button"
                            onClick={() => handleRemoveItem(index)}
                            className="inline-flex items-center gap-1 text-xs font-semibold text-red-600 hover:text-red-700"
                            disabled={items.length === 1}
                          >
                            <Trash2 className="h-4 w-4" />
                            Xóa
                          </button>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td
                        colSpan={7}
                        className="px-4 py-6 text-center text-gray-500"
                      >
                        Chưa có dòng chi tiết. Thêm mới để bắt đầu.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <p className="text-sm text-gray-500">
                Tổng cộng:{" "}
                <span className="font-semibold text-gray-900">
                  {totalAmount.toLocaleString("vi-VN")} đ
                </span>
              </p>
              <button
                type="button"
                onClick={handleSubmit}
                className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-blue-700 disabled:opacity-70"
                disabled={isSaving}
              >
                <Save className="h-4 w-4" />
                {isSaving ? "Đang lưu..." : "Lưu phiếu thu"}
              </button>
            </div>
          </div>
        </form>
      </div>
    </>
  );
};

export default ReceiptDetail;
